import { API_URL, buildKioskAuthHeaders } from './api';

const DEFAULT_PORT = 8008;
const DEFAULT_SECURE_PORT = 8043;
const DEFAULT_DEVICE_ID = 'XBVW019042';

// Share the menu's unit price in cents between its choices, for orders and tickets.
export function buildMenuPriceLines(item) {
  const choices = (item.customSelections || []).flatMap((selection) => {
    const products = Array.isArray(selection.products)
      ? selection.products
      : selection.product ? [selection.product] : [];
    return products.map((product) => ({ selection, product }));
  });
  const unitCents = Math.round(Number(item.totalPrice || 0) * 100);
  return choices.map((choice, index) => {
    const cents = Math.floor(unitCents / choices.length) + (index < unitCents % choices.length ? 1 : 0);
    return { ...choice, unitPrice: cents / 100, totalPrice: cents * Number(item.quantity || 1) / 100 };
  });
}

function normalizeTicketLines(values = []) {
  return values
    .map((value) => String(value || '').trim())
    .filter(Boolean);
}

function getTicketStoreLines(options = {}) {
  return normalizeTicketLines([
    options.ticketStoreLine1,
    options.ticketStoreLine2,
    options.ticketStoreLine3,
    options.ticketStoreLine4,
    options.ticketStoreLine5,
    options.ticketStoreLine6,
  ]);
}

function getTicketFooterLines(options = {}) {
  return normalizeTicketLines([
    options.ticketFooterLine1,
    options.ticketFooterLine2,
    options.ticketFooterLine3,
  ]);
}

function getPaymentMethodLabel(paymentMethod, isAlreadyPaid = true) {
  const normalized = String(paymentMethod || '').trim().toLowerCase();
  if (normalized === 'cb') {
    return 'PAYE CB';
  }
  if (normalized === 'espece' || normalized === 'cash') {
    return '';
  }
  return '';
}

function normalizeMyposReceipt(receipt = {}) {
  const source = receipt && typeof receipt === 'object' ? receipt : {};
  const state = String(source.state || source.status || '').trim().toLowerCase();
  return {
    state,
    approved: state === 'approved' || state === 'ok' || source.approved === true,
    cardBrand: String(source.cardBrand || source.cardScheme || source.aidName || '').trim(),
    maskedPan: String(source.maskedPan || source.masked_pan || '').trim(),
    amount: source.amount ?? source.total ?? null,
    currency: String(source.currency || 'EUR').trim().toUpperCase() || 'EUR',
    authCode: String(source.authCode || source.auth_code || '').trim(),
    rrn: String(source.rrn || '').trim(),
    stan: String(source.stan || '').trim(),
    tid: String(source.tid || '').trim(),
    mid: String(source.mid || '').trim(),
    aid: String(source.aid || '').trim(),
    paymentReference: String(source.paymentReference || source.payment_reference || '').trim(),
    transactionDate: String(source.transactionDate || source.merchantTransactionDate || '').trim(),
    transactionTime: String(source.transactionTime || source.merchantTransactionTime || '').trim(),
  };
}

function shouldPrintMyposReceipt(options = {}, ticketType = 'client') {
  const receipt = normalizeMyposReceipt(options.myposReceipt || {});
  const paymentMethod = String(options.paymentMethod || '').trim().toLowerCase();
  const isAlreadyPaid = options.isAlreadyPaid !== undefined ? !!options.isAlreadyPaid : true;
  return ticketType === 'client' && paymentMethod === 'cb' && isAlreadyPaid && receipt.approved;
}

function addMyposReceiptSection(printer, options = {}) {
  if (!shouldPrintMyposReceipt(options, options.ticketType || 'client')) {
    return;
  }
  const receipt = normalizeMyposReceipt(options.myposReceipt || {});
  const amount = receipt.amount !== null && receipt.amount !== undefined && receipt.amount !== ''
    ? Number(receipt.amount).toFixed(2)
    : Number(options.total || 0).toFixed(2);

  printer.addFeedLine(2);
  printer.addText('------------------------------\n');
  printer.addTextAlign(printer.ALIGN_CENTER);
  printer.addTextDouble(true, false);
  printer.addText('RECU CARTE BANCAIRE\n');
  printer.addTextDouble(false, false);
  printer.addText('PAIEMENT ACCEPTE\n');
  printer.addTextAlign(printer.ALIGN_LEFT);
  if (receipt.cardBrand) printer.addText(`Carte: ${receipt.cardBrand}\n`);
  if (receipt.maskedPan) printer.addText(`PAN: ${receipt.maskedPan}\n`);
  printer.addText(`Montant: ${amount} ${receipt.currency}\n`);
  if (receipt.transactionDate || receipt.transactionTime) {
    printer.addText(`Date: ${[receipt.transactionDate, receipt.transactionTime].filter(Boolean).join(' ')}\n`);
  }
  if (receipt.tid) printer.addText(`TID: ${receipt.tid}\n`);
  if (receipt.mid) printer.addText(`MID: ${receipt.mid}\n`);
  if (receipt.authCode) printer.addText(`Autorisation: ${receipt.authCode}\n`);
  if (receipt.rrn) printer.addText(`RRN: ${receipt.rrn}\n`);
  if (receipt.stan) printer.addText(`STAN: ${receipt.stan}\n`);
  if (receipt.aid) printer.addText(`AID: ${receipt.aid}\n`);
  if (receipt.paymentReference) printer.addText(`Ref: ${receipt.paymentReference}\n`);
  printer.addTextAlign(printer.ALIGN_CENTER);
  printer.addText('A conserver\n');
}

// A Stripe receipt is kept distinct from myPOS fields: a PaymentIntent is not a TID or RRN.
export function normalizeStripeReceipt(receipt, total) {
  if (!receipt || receipt.provider !== 'stripe_terminal' || receipt.status !== 'succeeded'
    || !/^pi_[A-Za-z0-9]+$/.test(receipt.paymentIntentId || '')
    || !/^ch_[A-Za-z0-9]+$/.test(receipt.chargeId || '')
    || !Number.isSafeInteger(receipt.amountCents) || receipt.amountCents < 50
    || receipt.amountCents !== Math.round(Number(total) * 100)
    || String(receipt.currency).toLowerCase() !== 'eur' || !/^\d{4}$/.test(receipt.last4 || '')) return null;
  const text = value => [...String(value || '')].filter(character => character.charCodeAt(0) >= 32 && character.charCodeAt(0) !== 127).join('').slice(0, 120);
  const safeReceipt = {};
  ['authorization_code', 'application_preferred_name', 'dedicated_file_name', 'transaction_status_information',
    'application_cryptogram', 'terminal_verification_results', 'transaction_trace_number', 'siret_number',
    'bank_code', 'acceptor_identifier', 'acceptance_system_terminal_application_identifier',
    'card_acceptor_logical_number', 'system_acceptor_logical_number'].forEach(key => {
    if (receipt.receipt?.[key]) safeReceipt[key] = text(receipt.receipt[key]);
  });
  const network = text(receipt.network).toLowerCase();
  if (network === 'cartes_bancaires' && ['transaction_trace_number', 'siret_number', 'bank_code',
    'acceptor_identifier', 'acceptance_system_terminal_application_identifier', 'card_acceptor_logical_number',
    'system_acceptor_logical_number'].some(key => !safeReceipt[key]?.trim())) return null;
  return { provider: 'stripe_terminal', status: 'succeeded', paymentIntentId: receipt.paymentIntentId,
    chargeId: receipt.chargeId, amountCents: receipt.amountCents, currency: 'eur',
    brand: text(receipt.brand), last4: receipt.last4, network, receipt: safeReceipt };
}

function addStripeReceiptSection(printer, options) {
  if (options.ticketType !== 'client' || options.paymentMethod !== 'cb' || options.isAlreadyPaid === false) return;
  const receipt = normalizeStripeReceipt(options.stripeReceipt, options.total);
  if (!receipt) return;
  printer.addFeedLine(2);
  printer.addText('------------------------------\nRECU CARTE BANCAIRE\nPAIEMENT ACCEPTE\n');
  printer.addText(`Carte: ${receipt.brand} **** ${receipt.last4}\n`);
  printer.addText(`Montant: ${(receipt.amountCents / 100).toFixed(2)} EUR\n`);
  printer.addText(`Ref Stripe: ${receipt.paymentIntentId}\n`);
  const labels = { authorization_code: 'Autorisation', application_preferred_name: 'Application',
    dedicated_file_name: 'AID', transaction_status_information: 'TSI', application_cryptogram: 'Cryptogramme',
    terminal_verification_results: 'TVR', transaction_trace_number: 'Trace', siret_number: 'SIRET',
    bank_code: 'Banque', acceptor_identifier: 'Accepteur', acceptance_system_terminal_application_identifier: 'Terminal',
    card_acceptor_logical_number: 'Accepteur logique', system_acceptor_logical_number: 'Systeme accepteur' };
  Object.entries(receipt.receipt).forEach(([key, value]) => printer.addText(`${labels[key]}: ${value}\n`));
  printer.addText('A conserver\n');
}

function getTicketScopeLabel(ticketType = 'kitchen', productionZoneName = '') {
  if (String(ticketType || '').toLowerCase() === 'client') {
    return 'TICKET CLIENT';
  }
  return String(productionZoneName || '').trim();
}

function roundCurrency(value) {
  return Number(Number(value || 0).toFixed(2));
}

function normalizeTaxRateValue(value, fallback = 10) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return Number(fallback);
  }
  return Math.max(0, Math.min(100, roundCurrency(parsed)));
}

function computeTaxTotalsFromLines(lines = []) {
  const grouped = new Map();

  (lines || []).forEach((line) => {
    const ttc = Math.max(0, Number(line?.ttc || 0));
    if (!ttc) {
      return;
    }

    const taxRate = normalizeTaxRateValue(line?.taxRate, 10);
    const divisor = 1 + (taxRate / 100);
    const ht = taxRate > 0 ? ttc / divisor : ttc;
    const tva = ttc - ht;
    const key = taxRate.toFixed(2);
    const current = grouped.get(key) || {
      rate: taxRate,
      totalTtc: 0,
      totalHt: 0,
      totalTva: 0,
    };

    current.totalTtc += ttc;
    current.totalHt += ht;
    current.totalTva += tva;
    grouped.set(key, current);
  });

  const breakdown = [...grouped.values()]
    .map((row) => ({
      rate: roundCurrency(row.rate),
      totalTtc: roundCurrency(row.totalTtc),
      totalHt: roundCurrency(row.totalHt),
      totalTva: roundCurrency(row.totalTva),
    }))
    .sort((a, b) => a.rate - b.rate);

  return {
    breakdown,
    totalTtc: roundCurrency(breakdown.reduce((sum, row) => sum + row.totalTtc, 0)),
    totalHt: roundCurrency(breakdown.reduce((sum, row) => sum + row.totalHt, 0)),
    totalTva: roundCurrency(breakdown.reduce((sum, row) => sum + row.totalTva, 0)),
  };
}

function computeTicketTaxTotals(items = [], totalTtc = 0) {
  const rawLines = [];

  (items || []).forEach((item) => {
    const quantity = Math.max(0, Number(item?.quantity || 0));
    if (!quantity) {
      return;
    }

    const itemUnitPrice = Math.max(0, Number(item?.totalPrice || 0));
    if (!itemUnitPrice) {
      return;
    }

    if (item?.isMenu) {
      const choices = buildMenuPriceLines(item);
      if (choices.length) {
        choices.forEach(({ product, totalPrice }) => rawLines.push({
          ttc: totalPrice,
          taxRate: normalizeTaxRateValue(product?.taxRate ?? product?.Product?.taxRate, 10),
        }));
      } else {
        rawLines.push({ ttc: itemUnitPrice * quantity, taxRate: normalizeTaxRateValue(item.taxRate, 10) });
      }
      return;
    }

    rawLines.push({
      ttc: itemUnitPrice * quantity,
      taxRate: normalizeTaxRateValue(item?.taxRate ?? item?.Product?.taxRate, 10),
    });
  });

  const rawTotal = rawLines.reduce((sum, line) => sum + line.ttc, 0);
  const normalizedTargetTotal = Math.max(0, Number(totalTtc || 0));
  const ratio = rawTotal > 0 && normalizedTargetTotal >= 0
    ? normalizedTargetTotal / rawTotal
    : 1;
  const adjustedLines = rawLines.map((line) => ({
    ...line,
    ttc: line.ttc * ratio,
  }));

  return computeTaxTotalsFromLines(adjustedLines);
}

function getConnectionTargets(settings) {
  const rawHost = String(settings?.networkHost || '').trim();
  const normalizedHost = rawHost.replace(/^https?:\/\//i, '').replace(/:\d+$/, '').replace(/\/$/, '');
  const candidates = [
    String(settings?.printerIp || '').trim(),
    normalizedHost,
    normalizedHost && !normalizedHost.includes('.') ? `${normalizedHost}.local` : '',
  ].filter(Boolean);

  return [...new Set(candidates)];
}

function isEscPosNetworkPrinter(printer) {
  const mode = String(printer?.connectionMode || '').toLowerCase();
  return mode === 'escpos_tcp' || mode === 'esp32_bridge';
}

function loadEposScript() {
  return new Promise((resolve, reject) => {
    if (window.epson?.ePOSDevice) {
      resolve();
      return;
    }

    const existingScript = document.querySelector('script[data-epos-script="true"]');
    if (existingScript) {
      existingScript.addEventListener('load', () => resolve(), { once: true });
      existingScript.addEventListener('error', () => reject(new Error('Chargement ePOS impossible')), {
        once: true,
      });
      return;
    }

    const script = document.createElement('script');
    script.src = `${process.env.PUBLIC_URL || ''}/epos-2.17.0.js`;
    script.async = true;
    script.dataset.eposScript = 'true';
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Chargement ePOS impossible'));
    document.body.appendChild(script);
  });
}

// A drawer pulse is independent from receipt printing and never reconnects/retries.
export async function pulseCashDrawer(action, { isCurrent = () => true } = {}) {
  const notSent = message => Object.assign(new Error(message), { drawerState: 'failed' });
  if (action?.type !== 'cash_drawer_pulse' || action.printer?.connectionMode !== 'epson_epos'
    || ![2, 5].includes(action.pin) || ![100, 200, 300].includes(action.pulseMs)) throw notSent('Commande tiroir invalide.');
  if (!Number.isFinite(Date.parse(action.expiresAt)) || Date.now() >= Date.parse(action.expiresAt)) throw notSent('Commande tiroir expirée.');
  let scriptTimer;
  try {
    await Promise.race([loadEposScript(), new Promise((_, reject) => { scriptTimer = window.setTimeout(() => reject(notSent('Chargement imprimante interrompu.')), 10000); })]);
  } finally { window.clearTimeout(scriptTimer); }
  if (!isCurrent()) throw notSent('Poste modifié : tiroir non commandé.');
  const settings = action.printer;
  // Match the server's reserved drawer endpoint, without legacy print fallbacks.
  const target = String(settings.networkHost || settings.printerIp || '').trim().toLowerCase().replace(/\.$/, '').replace(/^\[|\]$/g, '');
  if (!target) throw notSent('Adresse imprimante absente.');
  const configuredPort = Number(settings.port || DEFAULT_PORT);
  const secure = window.location.protocol === 'https:' || configuredPort === DEFAULT_SECURE_PORT;
  const port = secure ? DEFAULT_SECURE_PORT : configuredPort;
  return new Promise((resolve, reject) => {
    let settled = false;
    let dispatched = false;
    let pulseSent = false;
    let sent = false;
    const connection = new window.epson.ePOSDevice();
    const finish = (error) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      try { connection.disconnect?.(); } catch (_) { /* No reconnect. */ }
      if (error) reject(Object.assign(error, { drawerState: sent ? 'uncertain' : 'failed' })); else resolve();
    };
    const timer = window.setTimeout(() => finish(new Error('Ouverture du tiroir à vérifier.')), 12000);
    connection.connect(target, port, code => {
      if (settled || dispatched) return;
      if (!['OK', 'SSL_CONNECT_OK'].includes(code)) { finish(new Error('Connexion tiroir impossible.')); return; }
      // Mark before createDevice so even duplicate SDK callbacks cannot send twice.
      dispatched = true;
      connection.createDevice(settings.deviceId || DEFAULT_DEVICE_ID, connection.DEVICE_TYPE_PRINTER, { crypto: secure, buffer: false }, (printer, status) => {
        if (settled || pulseSent) return;
        pulseSent = true;
        if (status !== 'OK' || !printer) { finish(new Error('Imprimante tiroir indisponible.')); return; }
        const drawer = action.pin === 2 ? printer.DRAWER_1 : printer.DRAWER_2;
        const pulse = printer[`PULSE_${action.pulseMs}`];
        if (drawer === undefined || pulse === undefined || typeof printer.addPulse !== 'function') { finish(new Error('Impulsion tiroir non prise en charge.')); return; }
        printer.onreceive = result => finish(result?.success ? null : new Error('Ouverture du tiroir à vérifier.'));
        try {
          if (!isCurrent()) throw new Error('Poste modifié : tiroir non commandé.');
          if (Date.now() >= Date.parse(action.expiresAt)) throw new Error('Commande tiroir expirée.');
          printer.addPulse(drawer, pulse); sent = true; printer.send();
        } catch (error) { finish(error); }
      });
    });
  });
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    if (!src) {
      reject(new Error('Image introuvable'));
      return;
    }

    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error(`Impossible de charger l’image: ${src}`));
    image.src = src;
  });
}

async function addTicketLogo(printer, logoMedia, options = {}) {
  const src = String(logoMedia || '').trim();
  if (!src) {
    return;
  }

  const image = await loadImage(src);
  const maxWidth = Math.max(80, parseInt(options.maxWidth || 360, 10) || 360);
  const maxHeight = Math.max(40, parseInt(options.maxHeight || 180, 10) || 180);
  const ratio = Math.min(maxWidth / image.naturalWidth, maxHeight / image.naturalHeight, 1);
  const width = Math.max(1, Math.round(image.naturalWidth * ratio));
  const height = Math.max(1, Math.round(image.naturalHeight * ratio));
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');

  if (!context) {
    throw new Error('Canvas indisponible pour le logo du ticket');
  }

  context.fillStyle = '#ffffff';
  context.fillRect(0, 0, width, height);
  context.drawImage(image, 0, 0, width, height);
  printer.addImage(context, 0, 0, width, height, printer.COLOR_1, printer.MODE_MONO);
  printer.addFeedLine(1);
}

function withPrinter(settings, callback) {
  return new Promise((resolve, reject) => {
    const targets = getConnectionTargets(settings);
    const configuredPort = Number(settings?.port || DEFAULT_PORT) || DEFAULT_PORT;
    const isHttpsPage = window.location.protocol === 'https:';
    const usesSecurePort = isHttpsPage || configuredPort === DEFAULT_SECURE_PORT;
    const port = usesSecurePort ? DEFAULT_SECURE_PORT : configuredPort;
    const deviceId = settings?.deviceId || DEFAULT_DEVICE_ID;
    const timeoutMs = Number(settings?.connectionTimeoutMs || 12000) || 12000;
    const connectionDebugContext = `proto=${window.location.protocol} configuredPort=${configuredPort} effectivePort=${port} secureMode=${usesSecurePort ? 'on' : 'off'}`;
    let attemptIndex = 0;
    let settled = false;
    let timeoutId = null;
    let currentTarget = '';

    const settleResolve = () => {
      if (!settled) {
        if (timeoutId) {
          window.clearTimeout(timeoutId);
        }
        settled = true;
        resolve();
      }
    };

    const settleReject = (error) => {
      if (!settled) {
        if (timeoutId) {
          window.clearTimeout(timeoutId);
        }
        settled = true;
        reject(error);
      }
    };

    if (!targets.length) {
      settleReject(new Error('Adresse IP imprimante manquante'));
      return;
    }

    const tryConnect = () => {
      if (attemptIndex >= targets.length) {
        const attempted = targets.join(', ');
        settleReject(
          new Error(
            `Connexion imprimante échouée après ${targets.length} tentative(s). Cibles testées : ${attempted}. (${connectionDebugContext})`
          )
        );
        return;
      }

      currentTarget = targets[attemptIndex];
      attemptIndex += 1;
      const ePosDev = new window.epson.ePOSDevice();

      timeoutId = window.setTimeout(() => {
        tryConnect();
      }, timeoutMs);

      ePosDev.connect(currentTarget, port, (connectionCode) => {
        if (settled) {
          return;
        }

        if (connectionCode !== 'OK' && connectionCode !== 'SSL_CONNECT_OK') {
          // Keep a browser-side trace to speed up HTTPS/LAN troubleshooting.
          // eslint-disable-next-line no-console
          console.error('ePOS connect failed', {
            target: currentTarget,
            port,
            connectionCode,
            connectionDebugContext,
          });
          window.clearTimeout(timeoutId);
          tryConnect();
          return;
        }

        ePosDev.createDevice(
          deviceId,
          ePosDev.DEVICE_TYPE_PRINTER,
          { crypto: usesSecurePort, buffer: false },
          (printer, code) => {
            if (settled) {
              return;
            }

            if (code !== 'OK') {
              window.clearTimeout(timeoutId);
              settleReject(
                new Error(
                  `Connexion OK sur ${currentTarget}:${port}, mais createDevice a échoué pour "${deviceId}" : ${code}. (${connectionDebugContext})`
                )
              );
              return;
            }

            printer.onreceive = (result) => {
              if (result?.success) {
                settleResolve();
              } else {
                settleReject(
                  new Error(
                    `Impression échouée sur ${currentTarget}:${port} (${result?.code || 'erreur inconnue'}). (${connectionDebugContext})`
                  )
                );
              }
            };

            try {
              const result = callback(printer);
              if (result && typeof result.then === 'function') {
                result.catch(settleReject);
              }
            } catch (error) {
              settleReject(error instanceof Error ? error : new Error('Erreur impression'));
            }
          }
        );
      });
    };

    tryConnect();
  });
}

async function addStoreHeader(printer, options = {}) {
  const printedAt = new Date().toLocaleString('fr-FR');
  const ticketHeaderText = String(options.ticketHeaderText || '').trim();
  const ticketHeaderTextSize = Math.max(1, Math.min(8, Number(options.ticketHeaderTextSize) || 4));
  const storeLines = getTicketStoreLines(options);
  const topFeedLines = Math.max(0, parseInt(options.topFeedLines ?? 4, 10) || 0);
  const bottomFeedLines = Math.max(0, parseInt(options.bottomFeedLines ?? 3, 10) || 0);
  const skipPrintedAt = !!options.skipPrintedAt;

  printer.addTextAlign(printer.ALIGN_CENTER);
  if (topFeedLines > 0) {
    printer.addFeedLine(topFeedLines);
  }
  if (options.ticketLogoMedia) {
    // The logo is optional and should never block ticket printing.
    await addTicketLogo(printer, options.ticketLogoMedia, {
      maxWidth: options.ticketLogoMaxWidth,
      maxHeight: options.ticketLogoMaxHeight,
    }).catch(() => {});
  }
  storeLines.forEach((line) => {
    printer.addText(`${line}\n`);
  });
  if (storeLines.length) {
    printer.addFeedLine(1);
  }
  if (ticketHeaderText) {
    printer.addTextStyle(false, false, true);
    printer.addTextSize(ticketHeaderTextSize, ticketHeaderTextSize);
    printer.addText(`${ticketHeaderText}\n`);
    printer.addTextSize(1, 1);
    printer.addTextStyle(false, false, false);
    printer.addFeedLine(2);
  }
  if (!skipPrintedAt) {
    printer.addText(`${printedAt}\n`);
  }
  if (bottomFeedLines > 0) {
    printer.addFeedLine(bottomFeedLines);
  }
}

async function printEscPosTicket(printer, payload) {
  const endpoint = String(payload?.escposEndpoint || `/api/printers/${printer.id}/escpos-print`).trim();
  const normalizedPayload = { ...payload };
  delete normalizedPayload.escposEndpoint;
  const response = await fetch(`${API_URL}${endpoint}`, {
    method: 'POST',
    headers: buildKioskAuthHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(normalizedPayload),
  });
  const rawText = await response.text();
  let result = {};
  try {
    result = rawText ? JSON.parse(rawText) : {};
  } catch (error) {
    result = { error: rawText || 'Réponse serveur non JSON' };
  }
  if (!response.ok) {
    throw new Error(result.error || 'Impression ESC/POS impossible');
  }
}

function addTicketQrSection(printer, options = {}) {
  const qrText = String(options.ticketQrText || '').trim();
  const qrUrl = String(options.ticketQrUrl || '').trim();

  if (!qrUrl) {
    return;
  }

  printer.addFeedLine(1);
  if (qrText) {
    printer.addTextAlign(printer.ALIGN_CENTER);
    printer.addText(`${qrText}\n`);
  }

  try {
    printer.addTextAlign(printer.ALIGN_CENTER);
    printer.addSymbol(
      qrUrl,
      printer.SYMBOL_QRCODE_MODEL_2,
      printer.LEVEL_M,
      6,
      6,
      6
    );
    printer.addFeedLine(1);
  } catch (error) {
    printer.addText(`${qrUrl}\n`);
  }
}

function addTicketIdentityFooter(printer, ticketNumber, options = {}) {
  if (ticketNumber === undefined || ticketNumber === null) {
    return;
  }

  const formattedNumber = String(ticketNumber).padStart(4, '0');
  const customerFirstName = String(options.customerFirstName || '').trim();
  const paymentMethodLabel = getPaymentMethodLabel(options.paymentMethod, options.isAlreadyPaid);
  const scopeLabel = getTicketScopeLabel(options.ticketType, options.productionZoneName);

  printer.addTextAlign(printer.ALIGN_CENTER);
  printer.addFeedLine(3);
  if (customerFirstName) {
    printer.addText(`${customerFirstName}\n`);
  }
  if (scopeLabel) {
    printer.addText(`${scopeLabel}\n`);
  }
  if (paymentMethodLabel) {
    printer.addText(`${paymentMethodLabel}\n`);
  }
  if (customerFirstName || scopeLabel || paymentMethodLabel) {
    printer.addFeedLine(1);
  }
  printer.addTextDouble(true, true);
  printer.addText(`COMMANDE ${formattedNumber}\n`);
  printer.addTextDouble(false, false);
  printer.addFeedLine(1);
}

function addOrderSummarySection(printer, lines = []) {
  const summaryLines = Array.isArray(lines)
    ? lines
      .map((line) => ({
        quantity: Math.max(1, Number(line?.quantity || 1)),
        label: resolvePrintedLineLabel(line, 'kitchen'),
      }))
      .filter((line) => line.label)
    : [];

  if (!summaryLines.length) {
    return;
  }

  printer.addFeedLine(2);
  printer.addText('------------------------------\n');
  printer.addTextAlign(printer.ALIGN_LEFT);
  printer.addText('TOTAL COMMANDE\n');
  summaryLines.forEach((line) => {
    printer.addText(`   (${line.quantity} x ${line.label})\n`);
  });
  printer.addFeedLine(1);
}

function addOrderNotes(printer, options = {}) {
  const customerComment = String(options.customerComment || '').trim();
  const includeCutlery = !!options.includeCutlery;

  if (!customerComment && !includeCutlery) {
    return;
  }

  printer.addFeedLine(1);
  printer.addText('------------------------------\n');
  printer.addTextAlign(printer.ALIGN_LEFT);

  if (customerComment) {
    printer.addText('Commentaire client\n');
    printer.addText(`   ${customerComment}\n`);
  }

  if (includeCutlery) {
    printer.addText('   + Couverts demandes\n');
  }

  printer.addFeedLine(1);
}

function resolvePrintedLineLabel(line = {}, ticketType = 'kitchen') {
  const clientName = String(line.name || '').trim();
  const kitchenName = String(line.kitchenName || '').trim();
  const baseLabel = ticketType === 'client' ? clientName : (kitchenName || clientName);
  if (!line.isPromoGift) {
    return baseLabel;
  }
  return `🎁 ${baseLabel.replace(/^🎁\s*/u, '')}`;
}

function normalizePrinterGroups(cartItems, printers, productionZones = []) {
  const printersById = new Map(printers.map((printer) => [String(printer.id), printer]));
  const zonesById = new Map(productionZones.map((zone) => [String(zone.id), zone]));
  const defaultPrinter = printers.find((printer) => printer.isMain) || printers[0] || null;
  const grouped = new Map();

  const resolvePrinterForZone = (zone) => {
    if (!zone) {
      return defaultPrinter;
    }

    return (
      printersById.get(String(zone.printerId)) ||
      zone.Printer ||
      defaultPrinter
    );
  };

  const resolveZoneForLine = (line) => {
    const explicitZoneId = line.productionZoneId || line.ProductionZone?.id || line.zoneId;
    if (explicitZoneId && zonesById.has(String(explicitZoneId))) {
      return zonesById.get(String(explicitZoneId));
    }

    const explicitPrinterId = line.printerId || line.Printer?.id || line.product?.printerId || line.Product?.printerId;
    if (explicitPrinterId) {
      const matchingZone = productionZones.find((zone) => String(zone.printerId) === String(explicitPrinterId));
      if (matchingZone) {
        return matchingZone;
      }
      const printer = printersById.get(String(explicitPrinterId));
      if (printer) {
        return {
          id: `printer-${printer.id}`,
          name: printer.name || 'Imprimante',
          printerId: printer.id,
          ticketCopies: 1,
          Printer: printer,
        };
      }
    }

    const mainZone = productionZones.find((zone) => zone.Printer?.isMain) || productionZones[0] || null;
    if (mainZone) {
      return mainZone;
    }

    if (defaultPrinter) {
      return {
        id: `printer-${defaultPrinter.id}`,
        name: defaultPrinter.name || 'Imprimante',
        printerId: defaultPrinter.id,
        ticketCopies: 1,
        Printer: defaultPrinter,
      };
    }

    return null;
  };

  const pushLine = (zone, line) => {
    const printer = resolvePrinterForZone(zone);
    if (!printer) {
      return;
    }

    const key = String(zone?.id || zone?.printerId || printer.id);
    if (!grouped.has(key)) {
      grouped.set(key, {
        key,
        zone,
        printer,
        copies: Math.max(1, Number(zone?.ticketCopies || 1)),
        lines: [],
      });
    }
    grouped.get(key).lines.push(line);
  };

  cartItems.forEach((item) => {
    const baseLine = {
      name: item.name,
      kitchenName: item.kitchenName || item.Product?.kitchenName || '',
      quantity: Number(item.quantity || 1),
      lineTotal: Number((Number(item.totalPrice || 0) * Number(item.quantity || 1)).toFixed(2)),
      isMenuSelection: false,
      selectedSupplements: Array.isArray(item.selectedSupplements) ? item.selectedSupplements : [],
      isPromoGift: !!item.isPromoGift,
      productionZoneId: item.productionZoneId || item.ProductionZone?.id || null,
      printerId: item.printerId || item.Printer?.id || item.Product?.printerId || null,
    };

    if (item.isMenu && Array.isArray(item.customSelections) && item.customSelections.length) {
      buildMenuPriceLines(item).forEach(({ selection, product, totalPrice }) => {
        const line = {
          menuName: item.name,
          name: product.name || '',
          kitchenName: product.kitchenName || product.name || '',
          sectionName: selection.sectionName,
          quantity: Number(item.quantity || 1),
          lineTotal: totalPrice,
          isMenuSelection: true,
          selectedSupplements: [],
          isPromoGift: false,
          productionZoneId: product.productionZoneId || product.ProductionZone?.id || null,
          printerId: product.printerId || product.Printer?.id || null,
        };
        pushLine(resolveZoneForLine(line), line);
      });
      return;
    }

    pushLine(resolveZoneForLine(baseLine), baseLine);
  });

  const groups = [...grouped.values()];
  return groups.map((group) => {
    const otherOrderLines = groups
      .filter((candidate) => candidate.key !== group.key)
      .flatMap((candidate) => candidate.lines.map((line) => ({ ...line })));

    return {
      ...group,
      otherOrderLines,
    };
  });
}

export async function printTestTicket(settings) {
  if (isEscPosNetworkPrinter(settings)) {
    await printEscPosTicket(settings, {
      kind: 'test',
      printerName: settings.name || 'Imprimante',
      ticketHeaderText: '',
      ticketNumber: null,
      total: 0,
      lines: [],
      ticketQrText: '',
      ticketQrUrl: '',
    });
    return;
  }

  await loadEposScript();

  return withPrinter(settings, (printer) => {
    printer.addTextAlign(printer.ALIGN_CENTER);
    printer.addFeedLine(4);
    printer.addTextDouble(true, true);
    printer.addText('Ticket test\n');
    printer.addTextDouble(false, false);
    printer.addFeed();
    printer.addText('Impression OK\n');
    printer.addText(`IP : ${settings.printerIp}\n`);
    printer.addText(`Nom : ${settings.name || 'Imprimante'}\n`);
    printer.addText(new Date().toLocaleString('fr-FR'));
    printer.addFeedLine(5);
    printer.addCut(printer.CUT_FEED);
    printer.send();
  });
}

export async function printOrderTickets(printers, items, total, ticketNumber, options = {}) {
  if (options.stripeReceipt) {
    const receipt = normalizeStripeReceipt(options.stripeReceipt, total);
    if (!receipt) throw new Error('Reçu Stripe incomplet ou incohérent : récupérez le reçu dans Stripe. Aucune impression lancée.');
    options = { ...options, stripeReceipt: receipt };
  }
  const shouldPrintProductionTickets = !options.skipProductionTickets;
  const groups = normalizePrinterGroups(
    items,
    printers,
    shouldPrintProductionTickets ? (options.productionZones || []) : []
  );
  const ticketTaxTotals = computeTicketTaxTotals(items, total);

  if (!groups.length) {
    throw new Error('Aucune imprimante disponible pour cette commande.');
  }

  const printGroup = async ({ printer, zone, lines, copies, otherOrderLines = [] }, ticketType = 'kitchen') => {
    const productionZoneName = zone?.name || printer?.name || '';
    for (let copyIndex = 0; copyIndex < Math.max(1, Number(copies || 1)); copyIndex += 1) {
      if (isEscPosNetworkPrinter(printer)) {
        // eslint-disable-next-line no-await-in-loop
        await printEscPosTicket(printer, {
          kind: 'order',
          ticketType,
          productionZoneName,
          ticketHeaderText: options.ticketHeaderText || '',
          ticketNumber,
          total,
          lines,
          orderSummaryLines: ticketType === 'client' ? [] : otherOrderLines,
          ticketQrText: options.ticketQrText || '',
          ticketQrUrl: options.ticketQrUrl || '',
          ticketStoreLine1: options.ticketStoreLine1 || '',
          ticketStoreLine2: options.ticketStoreLine2 || '',
          ticketStoreLine3: options.ticketStoreLine3 || '',
          ticketStoreLine4: options.ticketStoreLine4 || '',
          ticketStoreLine5: options.ticketStoreLine5 || '',
          ticketStoreLine6: options.ticketStoreLine6 || '',
          ticketFooterLine1: options.ticketFooterLine1 || '',
          ticketFooterLine2: options.ticketFooterLine2 || '',
          ticketFooterLine3: options.ticketFooterLine3 || '',
          totalHt: ticketTaxTotals.totalHt,
          totalTva: ticketTaxTotals.totalTva,
          customerFirstName: options.customerFirstName || '',
          paymentMethod: options.paymentMethod || '',
          isAlreadyPaid: options.isAlreadyPaid !== undefined ? !!options.isAlreadyPaid : undefined,
          myposReceipt: options.myposReceipt || null,
          stripeReceipt: ticketType === 'client' ? options.stripeReceipt || null : null,
          customerComment: options.customerComment || '',
          includeCutlery: !!options.includeCutlery,
        });
        continue;
      }

      // eslint-disable-next-line no-await-in-loop
      await loadEposScript();
      // eslint-disable-next-line no-await-in-loop
      await withPrinter(printer, async (device) => {
        await addStoreHeader(device, options);
        device.addTextAlign(device.ALIGN_LEFT);

        lines.forEach((line) => {
          device.addTextDouble(true, true);
          const lineLabel = resolvePrintedLineLabel(line, ticketType);
          device.addText(`${line.quantity} x ${lineLabel}\n`);
          device.addTextDouble(false, false);

          if (line.isMenuSelection) {
            device.addText(`   Menu: ${line.menuName}\n`);
            device.addText(`   Section: ${line.sectionName}\n`);
          }

          if (Array.isArray(line.selectedSupplements) && line.selectedSupplements.length > 0) {
            line.selectedSupplements.forEach((supplement) => {
              device.addText(`   + ${supplement.name} (${Number(supplement.price || 0).toFixed(2)} EUR)\n`);
            });
          }

          if (line.isPromoGift) {
            device.addText('   Cadeau offert\n');
          }

          device.addText(`   ${Number(line.lineTotal).toFixed(2)} EUR\n`);
          device.addFeedLine(1);
        });

        if (ticketType !== 'client') {
          addOrderSummarySection(device, otherOrderLines);
        }
        addOrderNotes(device, options);
        device.addText('------------------------------\n');
        device.addTextAlign(device.ALIGN_CENTER);
        device.addTextDouble(true, true);
        device.addText(`TOTAL TTC ${total} EUR\n`);
        device.addTextDouble(false, false);
        if (ticketType === 'client') {
          device.addText(`Montant HT: ${Number(ticketTaxTotals.totalHt || 0).toFixed(2)} EUR\n`);
          device.addText(`Montant TVA: ${Number(ticketTaxTotals.totalTva || 0).toFixed(2)} EUR\n`);
        }
        device.addFeedLine(2);
        getTicketFooterLines(options).forEach((line) => {
          device.addText(`${line}\n`);
        });
        addTicketQrSection(device, options);
        addTicketIdentityFooter(device, ticketNumber, {
          ...options,
          ticketType,
          productionZoneName,
        });
        addMyposReceiptSection(device, {
          ...options,
          ticketType,
          total,
        });
        addStripeReceiptSection(device, { ...options, ticketType, total });
        device.addFeedLine(5);
        device.addCut(device.CUT_FEED);
        device.send();
      });
    }
  };

  const productionGroups = groups.filter((group) => group.lines.length > 0);

  if (shouldPrintProductionTickets) {
    for (const group of productionGroups) {
      // eslint-disable-next-line no-await-in-loop
      await printGroup(group, 'kitchen');
    }

    const clientTicketZoneId = options.clientTicketZoneId ? String(options.clientTicketZoneId) : '';
    if (options.receiptPrinterId) {
      const receiptPrinter = (printers || []).find(printer => String(printer.id) === String(options.receiptPrinterId));
      if (!receiptPrinter) throw new Error('Imprimante ticket client de ce poste indisponible.');
      await printGroup({ printer: receiptPrinter, zone: null, lines: productionGroups.flatMap(group => group.lines), copies: Math.max(1, Number(options.clientTicketCopies || 1)) }, 'client');
    } else if (clientTicketZoneId && !options.workstationBound) {
      const configuredZone = (options.productionZones || []).find(zone => String(zone.id) === clientTicketZoneId);
      const configuredPrinter = configuredZone && (printers || []).find(printer => String(printer.id) === String(configuredZone.printerId || configuredZone.Printer?.id));
      const targetGroup =
        productionGroups.find((group) => String(group.zone?.id || '') === clientTicketZoneId) ||
        (configuredPrinter ? { printer: configuredPrinter, zone: configuredZone } : null) ||
        productionGroups.find((group) => String(group.printer?.id || '') === clientTicketZoneId) ||
        null;

      if (targetGroup) {
        const clientLines = normalizePrinterGroups(items, printers, options.productionZones || [])
          .flatMap((group) => group.lines)
          .map((line) => ({ ...line }));
        await printGroup({
          printer: targetGroup.printer,
          zone: targetGroup.zone,
          lines: clientLines,
          copies: Math.max(1, Number(options.clientTicketCopies || 1)),
        }, 'client');
      }
    }
  } else {
    for (const group of productionGroups) {
      // eslint-disable-next-line no-await-in-loop
      await printGroup({
        ...group,
        copies: 1,
      }, 'client');
    }
  }
}

export async function printDailySummary(settings, summary) {
  const normalizedServiceSplit = summary?.serviceSplit && typeof summary.serviceSplit === 'object'
    ? summary.serviceSplit
    : null;

  const printServiceBlock = (printer, title, values = {}, emphasizeTotal = false) => {
    const commandsCount = Number(values?.ordersCount || 0);
    const byCard = Number(values?.paidByCardTtc || 0).toFixed(2);
    const byCash = Number(values?.paidByCashTtc || 0).toFixed(2);
    const totalTtc = Number(values?.totalTtc || 0).toFixed(2);

    printer.addText(`${title}\n`);
    printer.addText(`   Commandes: ${commandsCount}\n`);
    printer.addText(`   Regl. CB: ${byCard} EUR\n`);
    printer.addText(`   Regl. Especes: ${byCash} EUR\n`);
    if (emphasizeTotal) {
      printer.addTextDouble(true, true);
      printer.addText(`TOTAL TTC ${totalTtc} EUR\n`);
      printer.addTextDouble(false, false);
    } else {
      printer.addText(`   Total TTC: ${totalTtc} EUR\n`);
    }
    printer.addFeedLine(1);
  };

  if (isEscPosNetworkPrinter(settings)) {
    await printEscPosTicket(settings, {
      kind: 'daily-summary',
      printerName: settings.name || 'Imprimante',
      date: summary.date || '',
      ticketHeaderText: '',
      ticketNumber: null,
      total: summary.totalTtc || 0,
      paidByCardTtc: summary.paidByCardTtc || 0,
      paidByCashTtc: summary.paidByCashTtc || 0,
      totalHt: summary.totalHt || 0,
      totalTva: summary.totalTva || 0,
      vatBreakdown: Array.isArray(summary.vatBreakdown) ? summary.vatBreakdown : [],
      serviceSplit: normalizedServiceSplit || null,
      ordersCount: summary.ordersCount || 0,
      lines: Array.isArray(summary.items) ? summary.items.map((item) => ({
        name: item.name,
        quantity: item.quantity,
        lineTotal: item.totalTtc,
      })) : [],
      ticketQrText: '',
      ticketQrUrl: '',
    });
    return;
  }

  await loadEposScript();

  return withPrinter(settings, async (printer) => {
    const ticketLogoMedia = String(summary?.ticketLogoMedia || '').trim();
    await addStoreHeader(printer, {
      ticketLogoMedia,
      ticketLogoMaxWidth: 512,
      ticketLogoMaxHeight: 260,
      skipPrintedAt: true,
      topFeedLines: 1,
      bottomFeedLines: 1,
    });
    if (!ticketLogoMedia) {
      printer.addTextAlign(printer.ALIGN_CENTER);
      printer.addTextDouble(true, true);
      printer.addText('BEKIOSK\n');
      printer.addTextDouble(false, false);
      printer.addFeedLine(1);
    }
    printer.addTextAlign(printer.ALIGN_CENTER);
    printer.addTextDouble(true, true);
    printer.addText(`RECAP ${summary.date}\n`);
    printer.addTextDouble(false, false);
    printer.addFeedLine(2);

    printer.addTextAlign(printer.ALIGN_LEFT);
    summary.items.forEach((item) => {
      printer.addText(`${item.name}\n`);
      printer.addText(`   Qté: ${item.quantity}   TTC: ${Number(item.totalTtc).toFixed(2)} EUR\n`);
      printer.addFeedLine(1);
    });

    printer.addFeedLine(1);
    printer.addText('------------------------------\n');
    printServiceBlock(printer, 'TOTAL JOURNEE', normalizedServiceSplit?.total || {
      ordersCount: Number(summary.ordersCount || 0),
      paidByCardTtc: Number(summary.paidByCardTtc || 0),
      paidByCashTtc: Number(summary.paidByCashTtc || 0),
      totalTtc: Number(summary.totalTtc || 0),
    }, true);
    if (normalizedServiceSplit?.enabled) {
      printServiceBlock(printer, '1ER SERVICE', normalizedServiceSplit.firstService || {});
      printServiceBlock(printer, '2EME SERVICE', normalizedServiceSplit.secondService || {});
    }
    printer.addText(`TOTAL HT ${Number(summary.totalHt || 0).toFixed(2)} EUR\n`);
    printer.addText(`TOTAL TVA ${Number(summary.totalTva || 0).toFixed(2)} EUR\n`);
    if (Array.isArray(summary.vatBreakdown) && summary.vatBreakdown.length > 0) {
      summary.vatBreakdown.forEach((row) => {
        printer.addText(
          `TVA ${Number(row.rate || 0).toFixed(2)}% TTC ${Number(row.totalTtc || 0).toFixed(2)} EUR\n`
        );
        printer.addText(`   TVA ${Number(row.totalTva || 0).toFixed(2)} EUR\n`);
      });
    }
    printer.addFeedLine(6);
    printer.addCut(printer.CUT_FEED);
    printer.send();
  });
}

export async function printFidTicket(printerSettings, payload = {}) {
  const title = String(payload.title || 'PHOTO SOUVENIR').trim();
  const photoUrl = String(payload.photoUrl || '').trim();
  const ticketQrUrl = String(payload.ticketQrUrl || '').trim();
  const downloadQrUrl = String(payload.downloadQrUrl || '').trim();
  const downloadQrText = String(payload.downloadQrText || 'Telecharge ta photo sous 24h ici').trim();
  const fidLogoMedia = String(payload.fidLogoMedia || '').trim();

  if (isEscPosNetworkPrinter(printerSettings)) {
    await printEscPosTicket(printerSettings, {
      kind: 'fid-photo',
      title,
      fidLogoMedia,
      photoUrl,
      ticketQrUrl,
      downloadQrUrl,
      downloadQrText,
      escposEndpoint: '/api/fid/escpos-print',
    });
    return;
  }

  await loadEposScript();
  return withPrinter(printerSettings, async (printer) => {
    printer.addTextAlign(printer.ALIGN_CENTER);
    printer.addFeedLine(1);
    if (fidLogoMedia) {
      await addTicketLogo(printer, fidLogoMedia, { maxWidth: 420, maxHeight: 160 }).catch(() => {});
    }
    printer.addTextDouble(true, true);
    printer.addText(`${title}\n`);
    printer.addTextDouble(false, false);
    printer.addFeedLine(1);
    if (photoUrl) {
      await addTicketLogo(printer, photoUrl, { maxWidth: 512, maxHeight: 520 }).catch(() => {});
    }
    if (ticketQrUrl) {
      printer.addFeedLine(1);
      try {
        printer.addSymbol(ticketQrUrl, printer.SYMBOL_QRCODE_MODEL_2, printer.LEVEL_M, 6, 6, 6);
      } catch (error) {
        printer.addText(`${ticketQrUrl}\n`);
      }
    }
    if (downloadQrUrl) {
      printer.addFeedLine(1);
      printer.addText(`${downloadQrText}\n`);
      try {
        printer.addSymbol(downloadQrUrl, printer.SYMBOL_QRCODE_MODEL_2, printer.LEVEL_M, 6, 6, 6);
      } catch (error) {
        printer.addText(`${downloadQrUrl}\n`);
      }
    }
    printer.addFeedLine(5);
    printer.addCut(printer.CUT_FEED);
    printer.send();
  });
}
